import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdFileDownload, MdPrint } from 'react-icons/md';
import '../master/MasterData.css';

interface ExpenseByAccount {
  accountCode: string;
  accountName: string;
  accountType: string;
  currentMonth: number;
  previousMonth: number;
  yearToDate: number;
  variance: number;
  variancePercent: number;
}

interface ExpenseByDepartment {
  departmentCode: string;
  departmentName: string;
  salariesWages: number;
  materials: number;
  utilities: number;
  maintenance: number;
  otherExpenses: number;
  totalExpenses: number;
  budgetAmount: number;
  budgetVariance: number;
}

const ExpenseReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'account' | 'department'>('account');
  const [dateFrom, setDateFrom] = useState('2025-01-01');
  const [dateTo, setDateTo] = useState('2025-03-31');

  // Sample data - by account
  const expenseByAccount: ExpenseByAccount[] = [
    {
      accountCode: '5001',
      accountName: 'Cost of Goods Sold',
      accountType: 'Cost of Sales',
      currentMonth: 6468000,
      previousMonth: 6916000,
      yearToDate: 19509000,
      variance: -448000,
      variancePercent: -6.5
    },
    {
      accountCode: '6001',
      accountName: 'Salaries and Wages',
      accountType: 'Operating Expense',
      currentMonth: 2875000,
      previousMonth: 2875000,
      yearToDate: 8625000,
      variance: 0,
      variancePercent: 0.0
    },
    {
      accountCode: '6002',
      accountName: 'Employee Benefits',
      accountType: 'Operating Expense',
      currentMonth: 575000,
      previousMonth: 575000,
      yearToDate: 1725000,
      variance: 0,
      variancePercent: 0.0
    },
    {
      accountCode: '6101',
      accountName: 'Rent Expense',
      accountType: 'Operating Expense',
      currentMonth: 250000,
      previousMonth: 250000,
      yearToDate: 750000,
      variance: 0,
      variancePercent: 0.0
    },
    {
      accountCode: '6102',
      accountName: 'Utilities',
      accountType: 'Operating Expense',
      currentMonth: 185000,
      previousMonth: 165000,
      yearToDate: 535000,
      variance: 20000,
      variancePercent: 12.1
    },
    {
      accountCode: '6103',
      accountName: 'Depreciation',
      accountType: 'Operating Expense',
      currentMonth: 150000,
      previousMonth: 150000,
      yearToDate: 450000,
      variance: 0,
      variancePercent: 0.0
    },
    {
      accountCode: '6201',
      accountName: 'Marketing and Advertising',
      accountType: 'Operating Expense',
      currentMonth: 125000,
      previousMonth: 95000,
      yearToDate: 345000,
      variance: 30000,
      variancePercent: 31.6
    },
    {
      accountCode: '6301',
      accountName: 'Professional Fees',
      accountType: 'Operating Expense',
      currentMonth: 75000,
      previousMonth: 50000,
      yearToDate: 200000,
      variance: 25000,
      variancePercent: 50.0
    },
    {
      accountCode: '6401',
      accountName: 'Office Supplies',
      accountType: 'Operating Expense',
      currentMonth: 45000,
      previousMonth: 38000,
      yearToDate: 128000,
      variance: 7000,
      variancePercent: 18.4
    },
    {
      accountCode: '6501',
      accountName: 'Repairs and Maintenance',
      accountType: 'Operating Expense',
      currentMonth: 95000,
      previousMonth: 72000,
      yearToDate: 262000,
      variance: 23000,
      variancePercent: 31.9
    }
  ];

  // Sample data - by department
  const expenseByDepartment: ExpenseByDepartment[] = [
    {
      departmentCode: 'PROD',
      departmentName: 'Production',
      salariesWages: 1250000,
      materials: 6468000,
      utilities: 125000,
      maintenance: 68000,
      otherExpenses: 89000,
      totalExpenses: 8000000,
      budgetAmount: 7800000,
      budgetVariance: 200000
    },
    {
      departmentCode: 'SALES',
      departmentName: 'Sales & Marketing',
      salariesWages: 875000,
      materials: 0,
      utilities: 25000,
      maintenance: 12000,
      otherExpenses: 138000,
      totalExpenses: 1050000,
      budgetAmount: 1100000,
      budgetVariance: -50000
    },
    {
      departmentCode: 'ADMIN',
      departmentName: 'Administration',
      salariesWages: 625000,
      materials: 0,
      utilities: 30000,
      maintenance: 10000,
      otherExpenses: 85000,
      totalExpenses: 750000,
      budgetAmount: 800000,
      budgetVariance: -50000
    },
    {
      departmentCode: 'WAREHOUSE',
      departmentName: 'Warehouse & Logistics',
      salariesWages: 425000,
      materials: 0,
      utilities: 20000,
      maintenance: 18000,
      otherExpenses: 37000,
      totalExpenses: 500000,
      budgetAmount: 480000,
      budgetVariance: 20000
    }
  ];

  const totalAccount = expenseByAccount.reduce((acc, item) => ({
    currentMonth: acc.currentMonth + item.currentMonth,
    previousMonth: acc.previousMonth + item.previousMonth,
    yearToDate: acc.yearToDate + item.yearToDate,
    variance: acc.variance + item.variance
  }), { currentMonth: 0, previousMonth: 0, yearToDate: 0, variance: 0 });

  const totalDepartment = expenseByDepartment.reduce((acc, item) => ({
    salariesWages: acc.salariesWages + item.salariesWages,
    materials: acc.materials + item.materials,
    utilities: acc.utilities + item.utilities,
    maintenance: acc.maintenance + item.maintenance,
    otherExpenses: acc.otherExpenses + item.otherExpenses,
    totalExpenses: acc.totalExpenses + item.totalExpenses,
    budgetAmount: acc.budgetAmount + item.budgetAmount,
    budgetVariance: acc.budgetVariance + item.budgetVariance
  }), { salariesWages: 0, materials: 0, utilities: 0, maintenance: 0, otherExpenses: 0, totalExpenses: 0, budgetAmount: 0, budgetVariance: 0 });

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Expense Reports</h2>
            <p className="subtitle">Analyze expense trends by account and department</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handlePrint}>
              <MdPrint /> Print
            </button>
            <button className="btn btn-primary" onClick={handleExport}>
              <MdFileDownload /> Export to Excel
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">💸</div>
            <div className="summary-content">
              <p className="summary-label">Current Month</p>
              <p className="summary-value" style={{ color: '#dc3545' }}>
                ₱{totalAccount.currentMonth.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📅</div>
            <div className="summary-content">
              <p className="summary-label">Previous Month</p>
              <p className="summary-value">₱{totalAccount.previousMonth.toLocaleString()}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📊</div>
            <div className="summary-content">
              <p className="summary-label">Year to Date</p>
              <p className="summary-value">₱{totalAccount.yearToDate.toLocaleString()}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📈</div>
            <div className="summary-content">
              <p className="summary-label">Month Variance</p>
              <p className="summary-value" style={{ color: totalAccount.variance >= 0 ? '#dc3545' : '#28a745' }}>
                {totalAccount.variance >= 0 ? '+' : ''}₱{totalAccount.variance.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: '200px' }}>
              <label>Date From</label>
              <input
                type="date"
                  lang="en-US"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ minWidth: '200px' }}>
              <label>Date To</label>
              <input
                type="date"
                  lang="en-US"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-tabs" style={{ marginBottom: '20px' }}>
            <button
              className={`filter-tab ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              By Account
            </button>
            <button
              className={`filter-tab ${activeTab === 'department' ? 'active' : ''}`}
              onClick={() => setActiveTab('department')}
            >
              By Department
            </button>
          </div>

          {activeTab === 'account' && (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Account Code</th>
                    <th>Account Name</th>
                    <th>Type</th>
                    <th>Current Month</th>
                    <th>Previous Month</th>
                    <th>Year to Date</th>
                    <th>Variance</th>
                    <th>Variance %</th>
                  </tr>
                </thead>
                <tbody>
                  {expenseByAccount.map((item, index) => (
                    <tr key={index}>
                      <td className="code">{item.accountCode}</td>
                      <td className="name">{item.accountName}</td>
                      <td>{item.accountType}</td>
                      <td className="amount">₱{item.currentMonth.toLocaleString()}</td>
                      <td className="amount">₱{item.previousMonth.toLocaleString()}</td>
                      <td className="amount">₱{item.yearToDate.toLocaleString()}</td>
                      <td className="amount" style={{
                        color: item.variance >= 0 ? '#dc3545' : '#28a745',
                        fontWeight: 'bold'
                      }}>
                        {item.variance >= 0 ? '+' : ''}₱{item.variance.toLocaleString()}
                      </td>
                      <td style={{
                        textAlign: 'center',
                        color: item.variance >= 0 ? '#dc3545' : '#28a745',
                        fontWeight: 'bold'
                      }}>
                        {item.variancePercent >= 0 ? '+' : ''}{item.variancePercent.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td colSpan={3}>TOTAL:</td>
                    <td className="amount">₱{totalAccount.currentMonth.toLocaleString()}</td>
                    <td className="amount">₱{totalAccount.previousMonth.toLocaleString()}</td>
                    <td className="amount">₱{totalAccount.yearToDate.toLocaleString()}</td>
                    <td className="amount" style={{ color: totalAccount.variance >= 0 ? '#dc3545' : '#28a745' }}>
                      {totalAccount.variance >= 0 ? '+' : ''}₱{totalAccount.variance.toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'center', color: totalAccount.variance >= 0 ? '#dc3545' : '#28a745' }}>
                      {((totalAccount.variance / totalAccount.previousMonth) * 100).toFixed(1)}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {activeTab === 'department' && (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Dept Code</th>
                    <th>Department</th>
                    <th>Salaries & Wages</th>
                    <th>Materials</th>
                    <th>Utilities</th>
                    <th>Maintenance</th>
                    <th>Other</th>
                    <th>Total</th>
                    <th>Budget</th>
                    <th>Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {expenseByDepartment.map((item, index) => (
                    <tr key={index}>
                      <td className="code">{item.departmentCode}</td>
                      <td className="name">{item.departmentName}</td>
                      <td className="amount">₱{item.salariesWages.toLocaleString()}</td>
                      <td className="amount">₱{item.materials.toLocaleString()}</td>
                      <td className="amount">₱{item.utilities.toLocaleString()}</td>
                      <td className="amount">₱{item.maintenance.toLocaleString()}</td>
                      <td className="amount">₱{item.otherExpenses.toLocaleString()}</td>
                      <td className="amount" style={{ fontWeight: 'bold' }}>
                        ₱{item.totalExpenses.toLocaleString()}
                      </td>
                      <td className="amount">₱{item.budgetAmount.toLocaleString()}</td>
                      <td className="amount" style={{
                        color: item.budgetVariance >= 0 ? '#dc3545' : '#28a745',
                        fontWeight: 'bold'
                      }}>
                        {item.budgetVariance >= 0 ? '+' : ''}₱{item.budgetVariance.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                    <td colSpan={2}>TOTAL:</td>
                    <td className="amount">₱{totalDepartment.salariesWages.toLocaleString()}</td>
                    <td className="amount">₱{totalDepartment.materials.toLocaleString()}</td>
                    <td className="amount">₱{totalDepartment.utilities.toLocaleString()}</td>
                    <td className="amount">₱{totalDepartment.maintenance.toLocaleString()}</td>
                    <td className="amount">₱{totalDepartment.otherExpenses.toLocaleString()}</td>
                    <td className="amount">₱{totalDepartment.totalExpenses.toLocaleString()}</td>
                    <td className="amount">₱{totalDepartment.budgetAmount.toLocaleString()}</td>
                    <td className="amount" style={{ color: totalDepartment.budgetVariance >= 0 ? '#dc3545' : '#28a745' }}>
                      {totalDepartment.budgetVariance >= 0 ? '+' : ''}₱{totalDepartment.budgetVariance.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ExpenseReports;
